import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Cookie } from 'next-cookie';

import {
  EBOOK_ANNOTATIONS_DELETE_URL,
  EBOOK_ANNOTATIONS_FETCH_URL,
  EBOOK_ANNOTATIONS_SAVE_URL,
  EBOOK_ANNOTATIONS_UPDATE_URL,
  EBOOK_PROGRESS_GET_URL,
  EBOOK_PROGRESS_SAVE_URL,
} from '../../constants/ebook';
import { javaApi } from '../../utils/AxiosInstance';
import { getAnnotationId, toAnnotationRows } from '../../utils/ebook';

const initialState = {
  annotations: {
    loading: false,
    list: [],
    error: null,
  },
  lastPage: {
    loading: false,
    pageNumber: null,
    error: null,
  },
  saving: false,
};

/**
 * The five identifiers every eBook endpoint requires. `userId` is the numeric
 * account id and must match the bearer token's subject — the backend rejects a
 * mismatch — so it is read from the same cookie the token comes from.
 *
 * Verified against QA: javaApi's existing interceptor headers (Platform, token,
 * Authorization) are sufficient. The device_id / src / source / user_id headers
 * in the app's reference curl are not required.
 */
const identityOf = ({ courseId, assignedLessonId, parentId, contentId }) => {
  const cookies = new Cookie();

  return {
    courseId: Number(courseId) || courseId,
    userId: Number(cookies.get('userID')) || cookies.get('userID'),
    assignedLessonId: Number(assignedLessonId) || assignedLessonId,
    parentId: Number(parentId) || parentId,
    contentId: Number(contentId) || contentId,
  };
};

/** These endpoints answer with `{ success: 1 }` on write and `true` on read. */
const isOk = (payload) => payload?.success === 1 || payload?.success === true;

export const fetchAnnotations = createAsyncThunk('ebook/fetchAnnotations', async (identity, thunkAPI) => {
  try {
    // POST, not GET — `annotations/fetch` rejects GET.
    const response = await javaApi.post(EBOOK_ANNOTATIONS_FETCH_URL, identityOf(identity));

    return response?.data?.data?.annotations ?? [];
  } catch (error) {
    return thunkAPI.rejectWithValue('Could not load your notes and highlights.');
  }
});

/**
 * Creates one logical annotation. A multi-line selection is sent as several
 * rows in `annotations` — one per visual line — because the API models a single
 * rect per row.
 */
export const createAnnotation = createAsyncThunk('ebook/createAnnotation', async (payload, thunkAPI) => {
  const { identity, annotation } = payload;

  try {
    const response = await javaApi.post(EBOOK_ANNOTATIONS_SAVE_URL, {
      ...identityOf(identity),
      annotations: toAnnotationRows(annotation),
    });

    if (!isOk(response?.data)) {
      return thunkAPI.rejectWithValue(response?.data?.message ?? 'Could not save.');
    }

    // save does not return the new ids, and update/delete are keyed by
    // annotationId, so re-read to pick them up.
    await thunkAPI.dispatch(fetchAnnotations(identity));

    return true;
  } catch (error) {
    return thunkAPI.rejectWithValue('Could not save. Please try again.');
  }
});

/**
 * Edits a note. `update` takes the same envelope as `save` — the annotationId
 * lives inside each row, not in the URL — and it rejects an empty
 * `annotations` array outright.
 *
 * A note spanning N lines is stored as N rows, so every row in the group is
 * sent back with its own id.
 */
export const updateAnnotation = createAsyncThunk('ebook/updateAnnotation', async (payload, thunkAPI) => {
  const { identity, rows } = payload;

  if (!rows?.length) {
    return thunkAPI.rejectWithValue('Nothing to update.');
  }

  try {
    const response = await javaApi.put(EBOOK_ANNOTATIONS_UPDATE_URL, {
      ...identityOf(identity),
      annotations: rows,
    });

    if (!isOk(response?.data)) {
      return thunkAPI.rejectWithValue(response?.data?.message ?? 'Could not update.');
    }

    await thunkAPI.dispatch(fetchAnnotations(identity));

    return true;
  } catch (error) {
    return thunkAPI.rejectWithValue('Could not update. Please try again.');
  }
});

/**
 * Deletes by annotationId in the query string, one call per row. A multi-line
 * highlight therefore costs N calls — the API has no group identifier.
 */
export const deleteAnnotation = createAsyncThunk('ebook/deleteAnnotation', async (payload, thunkAPI) => {
  const { identity, ids } = payload;

  if (!ids?.length) {
    return thunkAPI.rejectWithValue('This item has no id from the server yet.');
  }

  try {
    await Promise.all(
      ids.map((annotationId) => javaApi.delete(EBOOK_ANNOTATIONS_DELETE_URL, { params: { annotationId } })),
    );

    await thunkAPI.dispatch(fetchAnnotations(identity));

    return true;
  } catch (error) {
    return thunkAPI.rejectWithValue('Could not delete. Please try again.');
  }
});

export const fetchLastReadPage = createAsyncThunk('ebook/fetchLastReadPage', async (identity) => {
  try {
    // GET here, unlike the annotations fetch.
    const response = await javaApi.get(EBOOK_PROGRESS_GET_URL, { params: identityOf(identity) });

    return Number(response?.data?.data?.pageNo) || null;
  } catch (error) {
    // No stored position is not an error — the reader just opens at page 1.
    return null;
  }
});

export const saveLastReadPage = createAsyncThunk('ebook/saveLastReadPage', async (payload, thunkAPI) => {
  const { identity, pageNumber } = payload;

  try {
    // The field is `pageNo`, not `pageNumber`.
    await javaApi.post(EBOOK_PROGRESS_SAVE_URL, { ...identityOf(identity), pageNo: pageNumber });

    return pageNumber;
  } catch (error) {
    return thunkAPI.rejectWithValue('Could not sync your reading position.');
  }
});

const ebookSlice = createSlice({
  name: 'ebook',
  initialState,
  reducers: {
    resetEbookState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnnotations.pending, (state) => {
        state.annotations.loading = true;
        state.annotations.error = null;
      })
      .addCase(fetchAnnotations.fulfilled, (state, action) => {
        state.annotations.loading = false;
        state.annotations.list = action.payload;
      })
      .addCase(fetchAnnotations.rejected, (state, action) => {
        state.annotations.loading = false;
        state.annotations.error = action.payload;
      })
      .addCase(fetchLastReadPage.pending, (state) => {
        state.lastPage.loading = true;
      })
      .addCase(fetchLastReadPage.fulfilled, (state, action) => {
        state.lastPage.loading = false;
        state.lastPage.pageNumber = action.payload;
      })
      .addCase(fetchLastReadPage.rejected, (state, action) => {
        state.lastPage.loading = false;
        state.lastPage.error = action.payload;
      })
      .addCase(saveLastReadPage.fulfilled, (state, action) => {
        state.lastPage.pageNumber = action.payload;
      });

    // One shared `saving` flag: the UI only ever has a single create/update/
    // delete in flight at a time.
    [createAnnotation, updateAnnotation, deleteAnnotation].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.saving = true;
          state.annotations.error = null;
        })
        .addCase(thunk.fulfilled, (state) => {
          state.saving = false;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.saving = false;
          state.annotations.error = action.payload;
        });
    });
  },
});

export const { resetEbookState } = ebookSlice.actions;

export const selectAnnotations = (state) => state.ebook.annotations;
export const selectEbookSaving = (state) => state.ebook.saving;
export const selectLastReadPage = (state) => state.ebook.lastPage;

export default ebookSlice.reducer;

export { getAnnotationId };
